import { ChangeDetectionStrategy, Component, computed, input, OnInit, output, signal } from '@angular/core';
import { RouterModule } from '@angular/router';
import { NgOptimizedImage } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { SidebarItemUiComponent } from '../sidebar-item/sidebar-item.ui-component';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [ButtonModule, NgOptimizedImage, RouterModule, SidebarItemUiComponent, Tooltip],
  templateUrl: './sidebar.ui-component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarUiComponent implements OnInit {
  readonly isAdmin = input.required<boolean>();
  readonly isCollapsed = signal<boolean>(false);
  readonly logout = output<void>();

  readonly fontSizes = [10, 12, 14];
  readonly selectedFontSize = signal<number>(14);
  readonly canIncrease = computed(() => this.fontSizes.indexOf(this.selectedFontSize()) < this.fontSizes.length - 1);
  readonly canDecrease = computed(() => this.fontSizes.indexOf(this.selectedFontSize()) > 0);

  ngOnInit(): void {
    const savedState = sessionStorage.getItem('sidebarCollapsed');
    if (savedState) {
      try {
        const isCollapsed = JSON.parse(savedState);
        this.isCollapsed.set(isCollapsed);
      } catch (error) {
        console.warn('Failed to parse sidebar state from session storage:', error);
      }
    }

    const savedFontSize = sessionStorage.getItem('fontSize');
    if (savedFontSize) {
      const fontSize = parseInt(savedFontSize, 10);
      if (this.fontSizes.includes(fontSize)) {
        this.selectedFontSize.set(fontSize);
      }
    }
    this.applyFontSize(this.selectedFontSize());
  }

  toggleSidebar(): void {
    this.isCollapsed.set(!this.isCollapsed());
    sessionStorage.setItem('sidebarCollapsed', JSON.stringify(this.isCollapsed()));
  }

  increaseFontSize(): void {
    const currentIndex = this.fontSizes.indexOf(this.selectedFontSize());
    if (currentIndex < this.fontSizes.length - 1) {
      const newSize = this.fontSizes[currentIndex + 1];
      this.selectedFontSize.set(newSize);
      sessionStorage.setItem('fontSize', newSize.toString());
      this.applyFontSize(newSize);
    }
  }

  decreaseFontSize(): void {
    const currentIndex = this.fontSizes.indexOf(this.selectedFontSize());
    if (currentIndex > 0) {
      const newSize = this.fontSizes[currentIndex - 1];
      this.selectedFontSize.set(newSize);
      sessionStorage.setItem('fontSize', newSize.toString());
      this.applyFontSize(newSize);
    }
  }

  private applyFontSize(fontSize: number): void {
    document.documentElement.style.fontSize = `${fontSize}px`;
  }
}
